/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import I18N from '@leverege/i18n';
import { Dialog, Flex, Pane, Text, Toast } from '@leverege/ui-elements';
import { DataSources } from '@leverege/ui-attributes';
import Molten from '@leverege/molten';
import { useSelector } from 'react-redux';
import { GlobalState } from '@leverege/ui-redux';
import { format } from 'date-fns';
import { VIN_CONFLICT_ERROR_CODES, MOVED_RESPONSE_MESSAGE } from '../constants';
import Loading from '../../../components/Loading';
import { canTransferVehicleToSiblingLocation } from '../../../util/Interface';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('recovr.views.pairing.ConfirmVinCollisionDialog');
function ConfirmVinCollisionDialog(props) {
  const {
    onClose,
    onCancel,
    show,
    errorCode,
    locationId,
    companyId,
    existingVehicleLocation,
    api,
    saleData,
    vin
  } = props;
  const [loading, setLoading] = useState(false);
  const [sameCompanyTransferDialog, setSameCompanyTransferDialog] = useState();
  const currentLocation = useSelector(state => {
    return state?.models?.lookup[locationId];
  });
  const systemId = Molten.getConfig('api/systemId');
  const closeDialogAndParent = () => {
    onClose();
    onCancel();
  };
  const goToSale = () => {
    const ref = companyId ? `${systemId}/company/${companyId}/locations/${saleData.data.location.id}/sales` : `${systemId}/location/${saleData.data.location.id}/sales`;
    const saleObj = {
      id: saleData.id,
      ref,
      type: 'sale'
    };
    const saleActions = DataSources.getActions(saleObj);
    closeDialogAndParent();
    GlobalState.dispatch(saleActions.goTo());
  };
  const canTransferFromLocation = async () => {
    const canTransfer = await canTransferVehicleToSiblingLocation({
      locationId,
      companyId
    });
    return canTransfer;
  };
  const getFormattedSaleDate = date => {
    const dateObject = new Date(date);
    return format(dateObject, 'MM/dd hh:mm a');
  };
  const handleVinCollisionAndClose = async ({
    closeParent = false
  }, collisionType) => {
    // call checkVinCollision
    setLoading(true);
    let response;
    try {
      const postData = {
        vin
      };
      let apiTarget;
      if (locationId) {
        if (companyId) {
          apiTarget = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId);
        } else {
          apiTarget = api.interface(systemId, 'location').obj(locationId);
        }
        response = await apiTarget.srv('recovrVin').post('claimExistingVin', postData, {
          type: collisionType
        });
      }
    } catch (err) {
      Toast.error(['Error when claiming VIN', err?.message]);
    } finally {
      const isTransfer = response?.message === MOVED_RESPONSE_MESSAGE;
      if (closeParent || isTransfer) {
        closeDialogAndParent();
        if (isTransfer) {
          Toast.success(['Vehicle successfully transferred to current location']);
        }
      } else {
        onClose();
      }
      setLoading(false);
    }
  };
  const cannotAddToInventory = () => {
    return /*#__PURE__*/_jsx(Dialog.Message, {
      show: show,
      onClose: closeDialogAndParent,
      icon: tIcon('cannotAddIcon'),
      title: t('vehicleInSystemTitle'),
      okay: t('closeOkay'),
      okayVariant: "primary",
      onOkay: closeDialogAndParent,
      children: /*#__PURE__*/_jsx(Text, {
        variant: "sectionBody",
        children: t('cannotAddMessage')
      })
    });
  };
  useEffect(() => {
    // if the vehicle is in the same company and has a location, show a dialog asking if they want to transfer it
    // only if the user has permission to move vehicles
    const sameCompanyTransfer = async () => {
      setLoading(true);
      setSameCompanyTransferDialog((await canTransferFromLocation()) ? /*#__PURE__*/_jsx(Dialog.Question, {
        show: show,
        onClose: closeDialogAndParent,
        icon: tIcon('vehicleTitleIcon'),
        title: t('vehicleInSystemTitle'),
        okay: t('setSameOkay'),
        okayVariant: "primary",
        cancelVariant: "secondary",
        onOkay: () => handleVinCollisionAndClose({
          closeParent: true
        }, 'dealer'),
        onCancel: closeDialogAndParent,
        children: /*#__PURE__*/_jsxs(Flex, {
          variant: "colL",
          children: [/*#__PURE__*/_jsxs(Text, {
            variant: "body",
            children: [t('setSameMessage'), " ", /*#__PURE__*/_jsx("b", {
              children: existingVehicleLocation.name
            }), "."]
          }), /*#__PURE__*/_jsx(Pane, {
            variant: "infoPane",
            children: /*#__PURE__*/_jsxs(Text, {
              variant: "infoText",
              style: {
                alignItems: 'flex-start'
              },
              children: [t('setSameMessageContinue'), " ", /*#__PURE__*/_jsx("b", {
                children: t('setSameMessageBold')
              })]
            })
          })]
        })
      }) : cannotAddToInventory());
      setLoading(false);
    };
    if (existingVehicleLocation.id && !sameCompanyTransferDialog) {
      sameCompanyTransfer();
    }
  }, []);
  switch (errorCode) {
    case VIN_CONFLICT_ERROR_CODES.CUSTOMER_VEHICLE_EXISTS:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: closeDialogAndParent,
          icon: tIcon('vehicleTitleIcon'),
          title: t('vehicleInSystemTitle'),
          okay: t('continueOkay'),
          okayVariant: "primaryDestructive",
          cancelVariant: "secondary",
          onOkay: () => handleVinCollisionAndClose({
            closeParent: false
          }, 'customer'),
          onCancel: closeDialogAndParent,
          children: /*#__PURE__*/_jsxs(Flex, {
            variant: "colL",
            children: [/*#__PURE__*/_jsxs(Text, {
              variant: "body",
              children: [t('vehicleExistsMsgSubtitle'), t('vehicleExistsMsg'), " ", /*#__PURE__*/_jsx("b", {
                children: t('vehicleExistsMsgBold')
              }), " ", t('vehicleExistsMsgContinued')]
            }), /*#__PURE__*/_jsx(Pane, {
              variant: "infoPane",
              children: /*#__PURE__*/_jsxs(Text, {
                variant: "alertText",
                style: {
                  alignItems: 'flex-start'
                },
                children: [t('confirmVin'), " ", /*#__PURE__*/_jsx("b", {
                  children: vin
                })]
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Loading, {
          message: t('vinProcessing'),
          loading: loading
        })]
      });
    case VIN_CONFLICT_ERROR_CODES.CUSTOMER_VEHICLE_EXISTS_SOLD_RECENTLY:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: closeDialogAndParent,
          icon: tIcon('vehicleTitleIcon'),
          title: t('vehicleCannotTitle'),
          okay: t('goToSaleOkay'),
          okayVariant: "primaryDestructive"
          // if companyId and locationId are both present, or if the saleData location matches the locationId, then enable the okay button
          ,
          okayDisabled: !(companyId && locationId || saleData.data.location?.id === locationId),
          cancelVariant: "secondary",
          onOkay: () => goToSale(saleData.id),
          onCancel: closeDialogAndParent,
          children: /*#__PURE__*/_jsxs(Flex, {
            variant: "colL",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: "body",
              children: t('soldRecentlyMsg', {
                voidWindow: saleData.voidWindow
              })
            }), /*#__PURE__*/_jsxs(Pane, {
              variant: "infoPane",
              children: [/*#__PURE__*/_jsxs(Text, {
                variant: "formTitle",
                style: {
                  alignItems: 'flex-start'
                },
                children: [saleData.data.vehicle.year, " ", saleData.data.vehicle.make, " ", saleData.data.vehicle.model, /*#__PURE__*/_jsx("br", {})]
              }), /*#__PURE__*/_jsx(Text, {
                variant: "bodySmallBold",
                style: {
                  alignItems: 'flex-start'
                },
                children: saleData.data.vehicle.vin
              }), /*#__PURE__*/_jsxs(Text, {
                variant: "bodysmall",
                style: {
                  alignItems: 'flex-start'
                },
                children: [t('saleLocation'), " ", /*#__PURE__*/_jsx("b", {
                  children: saleData.saleLocationName
                }), " ", /*#__PURE__*/_jsx("br", {}), t('stockNumber'), " ", /*#__PURE__*/_jsx("b", {
                  children: saleData.data.vehicle?.stocknumber
                }), /*#__PURE__*/_jsx("br", {}), t('dateOfSale'), " ", /*#__PURE__*/_jsx("b", {
                  children: getFormattedSaleDate(saleData.data.date)
                }), /*#__PURE__*/_jsx("br", {}), t('salesPerson'), " ", /*#__PURE__*/_jsx("b", {
                  children: saleData.data.salesperson
                }), /*#__PURE__*/_jsx("br", {}), t('customerName'), " ", /*#__PURE__*/_jsx("b", {
                  children: saleData.data.customer?.name
                })]
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(Loading, {
          message: t('vinProcessing'),
          loading: loading
        })]
      });
    case VIN_CONFLICT_ERROR_CODES.DEALER_VEHICLE_EXISTS_NOT_TRANSFERRABLE:
      return cannotAddToInventory();
    case VIN_CONFLICT_ERROR_CODES.DEALER_VEHICLE_EXISTS_SAME_LOCATION:
      return /*#__PURE__*/_jsx(Dialog.Message, {
        show: show,
        onClose: closeDialogAndParent,
        icon: tIcon('vehicleTitleIcon'),
        title: t('vehicleInSystemTitle'),
        okay: t('closeOkay'),
        okayVariant: "primary",
        onOkay: closeDialogAndParent,
        children: t('alreadyExistMsg')
      });
    case VIN_CONFLICT_ERROR_CODES.DEALER_VEHICLE_EXISTS_SAME_COMPANY:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [sameCompanyTransferDialog, /*#__PURE__*/_jsx(Loading, {
          message: t('vinProcessing'),
          loading: loading
        })]
      });
    case VIN_CONFLICT_ERROR_CODES.DEALER_VEHICLE_EXISTS_SAME_COMPANY_NO_LOCATION:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: closeDialogAndParent,
          icon: tIcon('vehicleTitleIcon'),
          title: t('vehicleInSystemTitle'),
          okay: t('addVehicleOkay'),
          okayVariant: "primary",
          cancelVariant: "secondary",
          onOkay: () => handleVinCollisionAndClose({
            closeParent: true
          }, 'dealer'),
          onCancel: closeDialogAndParent,
          children: /*#__PURE__*/_jsxs(Text, {
            variant: "body",
            children: [t('alreadyExistAtComp'), " ", /*#__PURE__*/_jsx("b", {
              children: t('alreadyExistAtCompBold', {
                name: currentLocation?.name
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Loading, {
          message: t('vinProcessing'),
          loading: loading
        })]
      });
    case VIN_CONFLICT_ERROR_CODES.DEALER_VEHICLE_EXISTS_TRANSFERRABLE:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Dialog.Question, {
          show: show,
          onClose: closeDialogAndParent,
          icon: tIcon('vehicleTitleIcon'),
          title: t('vehicleInSystemTitle'),
          okay: t('continueOkay'),
          okayVariant: "primaryDestructive",
          cancelVariant: "secondary",
          onOkay: () => handleVinCollisionAndClose({
            closeParent: false
          }, 'dealer'),
          onCancel: closeDialogAndParent,
          children: /*#__PURE__*/_jsxs(Flex, {
            variant: "colL",
            children: [/*#__PURE__*/_jsxs(Text, {
              variant: "body",
              children: [t('inUseElsewhere'), '\n', '\n', t('vehicleExistsMsg'), " ", /*#__PURE__*/_jsx("b", {
                children: t('vehicleExistsMsgBold')
              }), " ", t('vehicleExistsMsgContinued')]
            }), /*#__PURE__*/_jsx(Pane, {
              variant: "infoPane",
              children: /*#__PURE__*/_jsxs(Text, {
                variant: "alertText",
                style: {
                  alignItems: 'flex-start'
                },
                children: [t('confirmVin'), " ", /*#__PURE__*/_jsx("b", {
                  children: vin
                })]
              })
            })]
          })
        }), /*#__PURE__*/_jsx(Loading, {
          message: t('vinProcessing'),
          loading: loading
        })]
      });
    default:
      return /*#__PURE__*/_jsx(Dialog.Message, {
        show: show,
        onClose: closeDialogAndParent,
        icon: tIcon('vehicleTitleIcon'),
        title: t('errorTitle'),
        okay: t('closeOkay'),
        okayVariant: "primary",
        onOkay: closeDialogAndParent,
        children: t('errorMsg')
      });
  }
}
export default ConfirmVinCollisionDialog;